import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group"
import { Label } from "../components/ui/label"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function RadioRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Legacy pre-v2 radio blocks overload `element.variant` as the radio's
  // value carrier (see `const value = element.variant || element.text || ""`
  // below); that semantic is preserved and is separate from the theme-v2
  // data-variant contract.
  const atomDefaults = useAtomDefaults("radio")
  const resolvedVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const textSource = element.label ?? element.text
  const text = textSource ? String(resolveSymbol(textSource) ?? "") : undefined
  const group = (element as unknown as { group?: unknown }).group
  const name = group ? String(resolveSymbol(String(group)) ?? group) : element.name || element.id
  const value = String(
    resolveSymbol(String(element.value ?? element.variant ?? element.text ?? ""))
      ?? element.value
      ?? element.variant
      ?? element.text
      ?? "",
  )

  // Bind resolves the *group* value; this radio is checked when group === own value
  const [groupVal, setGroupVal] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  const handleChange = (newValue: string) => {
    setGroupVal(newValue)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: newValue })
  }

  // Single radio item wrapped in a minimal RadioGroup for proper shadcn styling
  return (
    <RadioGroup
      name={name}
      aria-label={text ?? name}
      value={element.bind !== undefined ? groupVal : undefined}
      onValueChange={handleChange}
      className={cn("flex items-center gap-2", stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))}
      data-testid={`radio-${element.id}`}
      data-action="input"
      data-field-type="radio"
      data-atom-type="radio"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-value={groupVal}
    >
      <RadioGroupItem id={element.id} value={value} aria-label={text ?? name} />
      {text && <Label htmlFor={element.id}>{text}</Label>}
    </RadioGroup>
  )
}
